import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { 
  Box, 
  Typography, 
  Paper, 
  Grid, 
  CircularProgress, 
  IconButton, 
  Card, 
  CardContent,
  Divider,
  Chip,
  Stack,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from "@mui/material";
import { 
  ChevronLeft, 
  ChevronRight,
  Calendar, 
  Clock, 
  Activity,
  UserCheck,
  Heart,
  TrendingUp,
  Download,
  ChevronDown
} from "lucide-react";
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip as RechartsTooltip, 
  ResponsiveContainer,
  ReferenceDot
} from "recharts";
import { kioskModel } from "../Axios/AxiosConfig";

// Custom Dot for Peaks defined outside to prevent re-creation on every render
const PeakDot = (props) => {
  const { cx, cy, payload } = props;
  if (payload && payload.is_peak === "1") {
    return (
      <circle cx={cx} cy={cy} r={3} fill="#FF3B30" stroke="white" strokeWidth={1} />
    );
  }
  return null;
};

const HeartSessionResult = () => {
  const { uid } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [sessions, setSessions] = useState([]);
  const [selectedSessionIndex, setSelectedSessionIndex] = useState(0);
  const [graphData, setGraphData] = useState([]);
  const [graphLoading, setGraphLoading] = useState(false);
  const [viewType, setViewType] = useState("ppg_filtered"); // or "ecg_filtered"
  const [selectedSegmentIndex, setSelectedSegmentIndex] = useState(0);

  // Fetch session history
  useEffect(() => {
    let isMounted = true;
    const fetchSessions = async () => {
      try {
        const res = await kioskModel.get(`api/v1/getClinicalHeartSessionsByPatientId/${uid}`);
        if (res.status === 200 && isMounted) {
          const data = Array.isArray(res.data) ? res.data : [res.data];
          // Create a new array and sort it
          const sortedData = [...data].sort((a, b) => {
            const timeA = new Date(a.createdAt || a.date || a.sessionId?.split('_')[1] || 0).getTime();
            const timeB = new Date(b.createdAt || b.date || b.sessionId?.split('_')[1] || 0).getTime();
            return timeB - timeA;
          });
          setSessions(sortedData);
        }
      } catch (err) {
        console.error("Error fetching sessions:", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchSessions();
    return () => { isMounted = false; };
  }, [uid]);

  const [timeWindow, setTimeWindow] = useState(0);

  // Fetch graph data for selected session
  useEffect(() => {
    if (sessions.length > 0) {
      let isMounted = true;
      const fetchGraphData = async () => {
        setGraphLoading(true);
        try {
          const sessionId = sessions[selectedSessionIndex].sessionId;
          const res = await kioskModel.get(`api/v1/getClinicalHeartSessionCSVData/${sessionId}?type=${viewType}`);
          if (res.status === 200 && res.data?.data && isMounted) {
            // Process data: convert strings to numbers
            const processed = res.data.data.map(d => ({
              ...d,
              time: parseFloat(d.time),
              filtered: parseFloat(d.filtered),
            }));
            setGraphData(processed);
            setTimeWindow(0); // Reset to start on session change
          }
        } catch (err) {
          console.error("Error fetching graph data:", err);
          if (isMounted) setGraphData([]);
        } finally {
          if (isMounted) setGraphLoading(false);
        }
      };

      fetchGraphData();
      return () => { isMounted = false; };
    }
  }, [selectedSessionIndex, sessions, viewType]);

  const currentSession = sessions[selectedSessionIndex];

  // Helper to split data into 5-second segments
  const segments = useMemo(() => {
    if (graphData.length === 0) return [];
    const maxTime = Math.max(...graphData.map(d => d.time));
    const segmentCount = Math.ceil(maxTime / 5);
    const results = [];
    for (let i = 0; i < segmentCount; i++) {
      const startTime = i * 5;
      const endTime = (i + 1) * 5;
      results.push({
        label: `${startTime}s - ${endTime}s`,
        startTime,
        endTime,
        data: graphData.filter(d => d.time >= startTime && d.time < endTime)
      });
    }
    return results;
  }, [graphData]);

  if (loading) {
    return (
      <Box className="flex flex-col items-center justify-center h-screen bg-[#FDFDFD]">
        <CircularProgress sx={{ color: "#DA6E0B" }} />
        <Typography className="mt-4 text-[#918165] font-medium animate-pulse">Fetching Heart Sessions...</Typography>
      </Box>
    );
  }

  if (!sessions || sessions.length === 0) {
    return (
      <Box className="flex flex-col items-center justify-center h-screen p-8 text-center bg-[#FDFDFD]">
        <img src="/icons/NoAppointment.png" alt="No data" className="w-48 mb-6 opacity-50" />
        <Typography variant="h5" className="text-[#3B362A] font-bold mb-2">No Heart Sessions Found</Typography>
        <Typography className="text-[#918165] mb-6">This patient has no recorded ECG or PPG sessions.</Typography>
        <Button 
          variant="outlined" 
          startIcon={<ChevronLeft />} 
          onClick={() => navigate(-1)}
          sx={{ borderColor: "#DA6E0B", color: "#DA6E0B", borderRadius: "10px", textTransform: "none" }}
        >
          Back to Profile
        </Button>
      </Box>
    );
  }

  return (
    <Box className="bg-[#F8F8F4] min-h-screen" style={{ fontFamily: "'Poppins', sans-serif" }}>
      {/* Premium Sticky Header */}
      <Box className="sticky top-0 z-10 bg-white/80 backdrop-blur-md border-bottom border-[#E7E7DB] px-8 py-4 flex items-center justify-between shadow-sm">
        <Box className="flex items-center gap-4">
          <IconButton 
            onClick={() => navigate(-1)}
            className="hover:bg-[#FBF1E7] transition-colors"
          >
            <ChevronLeft color="#DA6E0B" />
          </IconButton>
          <Box>
            <Typography variant="h5" className="font-bold text-[#3B362A]">
              Heart Analysis Dashboard
            </Typography>
            <Box className="flex items-center gap-2">
              <UserCheck size={14} className="text-[#DA6E0B]" />
              <Typography variant="caption" className="text-[#918165] font-medium">
                UID: {uid} • Latest Session: {new Date(sessions[0].createdAt || sessions[0].date).toLocaleDateString()}
              </Typography>
            </Box>
          </Box>
        </Box>
        <Chip 
          label={`${sessions.length} Heart Sessions`} 
          sx={{ bgcolor: "#FBF1E7", color: "#DA6E0B", fontWeight: 700, borderRadius: "8px" }} 
        />
      </Box>

      <Grid container sx={{ p: 4 }} spacing={4}>
        {/* Sidebar - Session Timeline */}
        <Grid item xs={12} md={3}>
          <Box className="sticky top-24" sx={{ 
            height: 'calc(100vh - 120px)', 
            overflowY: 'auto',
            pr: 2,
            '&::-webkit-scrollbar': { width: '4px' },
            '&::-webkit-scrollbar-thumb': { bgcolor: '#E7E7DB', borderRadius: '4px' }
          }}>
            <Typography variant="overline" className="text-[#918165] font-bold tracking-widest mb-4 block px-2">
              SESSION HISTORY
            </Typography>
            <Box className="relative pl-4 space-y-4 before:absolute before:left-[21px] before:top-0 before:bottom-0 before:w-[2px] before:bg-[#E7E7DB]">
              {sessions.map((session, index) => (
                <Box 
                  key={session.sessionId || index}
                  onClick={() => setSelectedSessionIndex(index)}
                  className={`relative pl-8 cursor-pointer group transition-all`}
                >
                  <Box className={`absolute left-0 top-1.5 w-3 h-3 rounded-full border-2 z-10 transition-all ${
                    selectedSessionIndex === index 
                    ? "bg-[#DA6E0B] border-[#DA6E0B] scale-125" 
                    : "bg-white border-[#C6B897] group-hover:border-[#DA6E0B]"
                  }`} />
                  
                  <Paper className={`p-4 rounded-xl transition-all border ${
                    selectedSessionIndex === index 
                    ? "bg-white border-[#DA6E0B] shadow-lg translate-x-2" 
                    : "bg-transparent border-transparent hover:bg-white hover:border-[#E7E7DB]"
                  }`}>
                    <Typography className={`text-sm font-bold ${selectedSessionIndex === index ? "text-[#DA6E0B]" : "text-[#3B362A]"}`}>
                      {new Date(session.createdAt || session.date).toLocaleDateString('en-IN', {
                        day: '2-digit', month: 'short', year: 'numeric'
                      })}
                    </Typography>
                    <Box className="flex items-center gap-2 mt-1">
                      <Clock size={12} className="text-[#918165]" />
                      <Typography variant="caption" className="text-[#918165]">
                        {new Date(session.createdAt || session.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </Typography>
                    </Box>
                  </Paper>
                </Box>
              ))}
            </Box>
          </Box>
        </Grid>

        {/* Main Workspace - Scrollable Graphs */}
        <Grid item xs={12} md={9}>
          <Card className="rounded-3xl border-none shadow-xl bg-white overflow-hidden mb-8">
            <Box className="bg-[#3B362A] p-6 text-white flex items-center justify-between">
              <Box className="flex items-center gap-4">
                <Box className="p-3 bg-white/10 rounded-2xl">
                  <Activity size={24} className="text-[#DA6E0B]" />
                </Box>
                <Box>
                  <Typography variant="h6" className="font-bold leading-none mb-1">
                    {viewType === "ecg_filtered" ? "ECG Continuous Analysis" : "PPG Continuous Analysis"}
                  </Typography>
                  <Typography variant="caption" className="text-gray-400">
                    Session ID: {currentSession?.sessionId}
                  </Typography>
                </Box>
              </Box>
              
              <ToggleButtonGroup
                value={viewType}
                exclusive
                onChange={(e, next) => next && setViewType(next)}
                size="small"
                sx={{ 
                  bgcolor: "rgba(255,255,255,0.05)",
                  p: 0.5,
                  borderRadius: "12px",
                  "& .MuiToggleButton-root": {
                    color: "white",
                    border: "none",
                    borderRadius: "8px !important",
                    mx: 0.5,
                    px: 2,
                    textTransform: "none",
                    "&.Mui-selected": {
                      bgcolor: "#DA6E0B",
                      color: "white",
                      "&:hover": { bgcolor: "#B85C09" }
                    }
                  }
                }}
              >
                {currentSession?.metrics?.ecg_bpm && (
                  <ToggleButton value="ecg_filtered">ECG</ToggleButton>
                )}
                <ToggleButton value="ppg_filtered">PPG</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Card>

          {graphLoading ? (
            <Box className="h-[400px] flex flex-col items-center justify-center bg-white rounded-3xl shadow-sm">
              <CircularProgress sx={{ color: "#DA6E0B" }} />
              <Typography className="mt-4 text-[#918165]">Processing full wave analysis...</Typography>
            </Box>
          ) : (
            <Box className="space-y-8">
              {/* Premium Metrics Section - Dynamic based on View Type */}
              {currentSession?.metrics && (
                <Grid container spacing={3}>
                  {viewType === "ecg_filtered" ? (
                    <>
                      <Grid item xs={12} sm={4}>
                        <Card className="rounded-3xl border-none shadow-md bg-white hover:shadow-lg transition-all p-6 border-t-4 border-[#DA6E0B]">
                          <Box className="flex justify-between items-start mb-2">
                            <Typography variant="overline" className="text-[#918165] font-bold">ECG BPM</Typography>
                            <Heart size={20} className="text-[#DA6E0B]" />
                          </Box>
                          <Typography variant="h3" className="font-black text-[#3B362A]">
                            {currentSession.metrics.ecg_bpm ? Math.round(currentSession.metrics.ecg_bpm) : "--"}
                            <span className="text-sm font-medium text-[#918165] ml-1">bpm</span>
                          </Typography>
                        </Card>
                      </Grid>

                      <Grid item xs={12} sm={4}>
                        <Card className="rounded-3xl border-none shadow-md bg-white hover:shadow-lg transition-all p-6 border-t-4 border-[#FF3B30]">
                          <Box className="flex justify-between items-start mb-2">
                            <Typography variant="overline" className="text-[#918165] font-bold">ECG HRV</Typography>
                            <Activity size={20} className="text-[#FF3B30]" />
                          </Box>
                          <Typography variant="h3" className="font-black text-[#3B362A]">
                            {currentSession.metrics.ecg_hrv ? Math.round(currentSession.metrics.ecg_hrv) : "--"}
                            <span className="text-sm font-medium text-[#918165] ml-1">ms</span>
                          </Typography>
                        </Card>
                      </Grid>

                      <Grid item xs={12} sm={4}>
                        <Card className="rounded-3xl border-none shadow-md bg-white hover:shadow-lg transition-all p-6 border-t-4 border-[#3B362A]">
                          <Box className="flex justify-between items-start mb-2">
                            <Typography variant="overline" className="text-[#918165] font-bold">ECG Quality</Typography>
                            <TrendingUp size={20} className="text-[#3B362A]" />
                          </Box>
                          <Typography variant="h3" className="font-black text-[#3B362A]" style={{ color: currentSession.metrics.ecg_quality >= 80 ? "#10B981" : "#DA6E0B" }}>
                            {currentSession.metrics.ecg_quality || "--"}
                            <span className="text-sm font-medium text-[#918165] ml-1">%</span>
                          </Typography>
                        </Card>
                      </Grid>
                    </>
                  ) : (
                    <>
                      <Grid item xs={12} sm={6} md={3}>
                        <Card className="rounded-3xl border-none shadow-md bg-white hover:shadow-lg transition-all p-6 border-t-4 border-[#DA6E0B]">
                          <Box className="flex justify-between items-start mb-2">
                            <Typography variant="overline" className="text-[#918165] font-bold">PPG HR</Typography>
                            <Heart size={20} className="text-[#DA6E0B]" />
                          </Box>
                          <Typography variant="h3" className="font-black text-[#3B362A]">
                            {currentSession.metrics.ppg_hr ? Math.round(currentSession.metrics.ppg_hr) : "--"}
                            <span className="text-sm font-medium text-[#918165] ml-1">bpm</span>
                          </Typography>
                        </Card>
                      </Grid>

                      <Grid item xs={12} sm={6} md={3}>
                        <Card className="rounded-3xl border-none shadow-md bg-white hover:shadow-lg transition-all p-6 border-t-4 border-[#10B981]">
                          <Box className="flex justify-between items-start mb-2">
                            <Typography variant="overline" className="text-[#918165] font-bold">PPG SDNN</Typography>
                            <Box className="p-1 rounded bg-[#E1F9F1]">
                              <TrendingUp size={16} className="text-[#10B981]" />
                            </Box>
                          </Box>
                          <Typography variant="h3" className="font-black text-[#3B362A]">
                            {currentSession.metrics.ppg_sdnn ? Math.round(currentSession.metrics.ppg_sdnn) : "--"}
                            <span className="text-sm font-medium text-[#918165] ml-1">ms</span>
                          </Typography>
                        </Card>
                      </Grid>

                      <Grid item xs={12} sm={6} md={3}>
                        <Card className="rounded-3xl border-none shadow-md bg-white hover:shadow-lg transition-all p-6 border-t-4 border-[#FF3B30]">
                          <Box className="flex justify-between items-start mb-2">
                            <Typography variant="overline" className="text-[#918165] font-bold">PPG RMSSD</Typography>
                            <Activity size={20} className="text-[#FF3B30]" />
                          </Box>
                          <Typography variant="h3" className="font-black text-[#3B362A]">
                            {currentSession.metrics.ppg_rmssd ? Math.round(currentSession.metrics.ppg_rmssd) : "--"}
                            <span className="text-sm font-medium text-[#918165] ml-1">ms</span>
                          </Typography>
                        </Card>
                      </Grid>

                      <Grid item xs={12} sm={6} md={3}>
                        <Card className="rounded-3xl border-none shadow-md bg-white hover:shadow-lg transition-all p-6 border-t-4 border-[#3B362A]">
                          <Box className="flex justify-between items-start mb-2">
                            <Typography variant="overline" className="text-[#918165] font-bold">PPG Quality</Typography>
                            <TrendingUp size={20} className="text-[#3B362A]" />
                          </Box>
                          <Typography variant="h3" className="font-black text-[#3B362A]" style={{ color: currentSession.metrics.ppg_quality >= 80 ? "#10B981" : "#DA6E0B" }}>
                            {currentSession.metrics.ppg_quality ? Math.round(currentSession.metrics.ppg_quality) : "--"}
                            <span className="text-sm font-medium text-[#918165] ml-1">%</span>
                          </Typography>
                        </Card>
                      </Grid>
                    </>
                  )}
                </Grid>
              )}

              <Accordion sx={{ bgcolor: 'transparent', boxShadow: 'none', '&:before': { display: 'none' } }}>
                <AccordionSummary
                  expandIcon={<ChevronDown className="text-[#918165]" />}
                  sx={{ px: 4, my: 2 }}
                >
                  <Typography variant="overline" className="text-[#918165] font-bold tracking-widest">
                    Waveform Analysis
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 0 }}>
                  {segments.length > 0 ? (
                    <Box className="space-y-6">
                      {segments.map((segment, index) => (
                        <Card key={index} className="rounded-3xl border-none shadow-md bg-white overflow-hidden">
                          <Box className="px-6 py-3 bg-[#F8F8F4] flex justify-between items-center border-b border-[#E7E7DB]">
                            <Typography variant="subtitle2" className="text-[#3B362A] font-bold">
                              Segment {index + 1}
                            </Typography>
                            <Chip 
                              label={segment.label} 
                              size="small" 
                              sx={{ bgcolor: "#3B362A", color: "white", fontWeight: 700, fontSize: '10px' }} 
                            />
                          </Box>
                          <CardContent className="p-6">
                            <Box sx={{ width: '100%', height: 280 }}>
                              <ResponsiveContainer>
                                <LineChart data={segment.data} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F1F1F1" />
                                  <XAxis 
                                    dataKey="time" 
                                    type="number"
                                    domain={[segment.startTime, segment.endTime]}
                                    tick={{ fontSize: 11, fill: '#918165' }}
                                    tickFormatter={(t) => `${t.toFixed(1)}s`}
                                    allowDataOverflow={true}
                                  />
                                  <YAxis 
                                    tick={{ fontSize: 11, fill: '#918165' }}
                                    domain={['auto', 'auto']}
                                  />
                                  <RechartsTooltip 
                                    contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }}
                                    labelFormatter={(t) => `Time: ${t.toFixed(3)}s`}
                                  />
                                  <Line 
                                    type="monotone" 
                                    dataKey="filtered" 
                                    stroke={viewType === "ecg_filtered" ? "#FF3B30" : "#10B981"} 
                                    strokeWidth={2} 
                                    dot={<PeakDot />}
                                    isAnimationActive={false}
                                  />
                                </LineChart>
                              </ResponsiveContainer>
                            </Box>
                          </CardContent>
                        </Card>
                      ))}
                    </Box>
                  ) : (
                    <Box className="h-[400px] flex flex-col items-center justify-center border-2 border-dashed border-[#E7E7DB] rounded-3xl bg-white">
                      <TrendingUp size={48} className="text-[#E7E7DB] mb-4" />
                      <Typography className="text-[#918165]">No wave data available for analysis.</Typography>
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>
            </Box>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default HeartSessionResult;
